import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { X } from 'lucide-react';
import { api } from '../../services/api';
import { legacyApi, type EstimateSummary, type SaleOrder } from '../../services/legacyApi';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField } from '../../components/forms/formValidationUtils';
import { formatSalesOrderRef } from './workOrderDisplay';

const schema = z.object({
    source_type: z.enum(['SO', 'ESTIMATE']),
    so_id: z.string().optional(),
    estimate_id: z.string().optional(),
    remarks: z.string().optional(),
}).superRefine((data, ctx) => {
    if (data.source_type === 'SO' && !data.so_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Sales Order is required',
            path: ['so_id'],
        });
    }
    if (data.source_type === 'ESTIMATE' && !data.estimate_id) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Estimate / Quote reference is required',
            path: ['estimate_id'],
        });
    }
});

type FormData = z.infer<typeof schema>;

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export function WorkOrderModal({ isOpen, onClose, onSuccess }: Props) {
    const [estimates, setEstimates] = useState<EstimateSummary[]>([]);
    const [saleOrders, setSaleOrders] = useState<SaleOrder[]>([]);

    const { register, handleSubmit, watch, formState: { errors, isSubmitting } } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            source_type: 'SO',
        }
    });
    const sourceType = watch('source_type');

    useEffect(() => {
        if (isOpen) {
            Promise.all([
                legacyApi.sales.listOrders(),
                legacyApi.estimates.list(),
            ])
                .then(([orders, estimateList]) => {
                    setSaleOrders(
                        (orders || []).filter((order) => {
                            const statusId = Number(order.so_status_id || 0);
                            return statusId >= 2 && statusId !== 7;
                        })
                    );
                    setEstimates(estimateList || []);
                })
                .catch(err => {
                    console.error('Error loading work order sources:', err);
                    toast.error('Failed to load work order sources: ' + err.message);
                });
        }
    }, [isOpen]);

    const onSubmit = async (data: FormData) => {
        try {
            const result = await api.manufacturing.createWorkOrder({
                ...(data.source_type === 'SO'
                    ? { so_id: parseInt(data.so_id || '', 10) }
                    : { estimate_id: parseInt(data.estimate_id || '', 10) }),
                remarks: data.remarks,
            });
            toast.success(`Work Order ${result.wo_number} created successfully!`);
            onSuccess();
        } catch (e: any) {
            console.error('Error creating work order:', e);
            toast.error(`Failed to create Work Order: ${e.message}`);
        }
    };
    const onInvalid = (formErrors: Record<string, any>) => {
        showValidationSummaryToast();
        focusFirstInvalidField(Object.keys(formErrors)[0]);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-lg font-semibold">New Work Order</h2>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="p-4 space-y-4 overflow-y-auto">
                    <div className="rounded-md border bg-slate-50 p-3 space-y-1">
                        <p className="text-xs text-slate-600">Flow: Sales Order {'->'} Work Order {'->'} Production.</p>
                        <p className="text-xs text-slate-500">
                            Sales Order is the production trigger. Estimate / Quote remains an optional costing reference when needed.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 rounded-md border bg-white p-1">
                        <label className={cn(
                            "cursor-pointer rounded px-3 py-2 text-sm border",
                            sourceType === 'SO' ? "border-blue-500 bg-blue-50 text-blue-700" : "border-transparent text-slate-600"
                        )}>
                            <input
                                type="radio"
                                value="SO"
                                className="sr-only"
                                {...register('source_type')}
                            />
                            Sales Order
                            <span className="block text-[11px] text-slate-500">Recommended</span>
                        </label>
                        <label className={cn(
                            "cursor-pointer rounded px-3 py-2 text-sm border",
                            sourceType === 'ESTIMATE' ? "border-blue-500 bg-blue-50 text-blue-700" : "border-transparent text-slate-600"
                        )}>
                            <input
                                type="radio"
                                value="ESTIMATE"
                                className="sr-only"
                                {...register('source_type')}
                            />
                            Estimate / Quote
                            <span className="block text-[11px] text-slate-500">Planning reference</span>
                        </label>
                    </div>

                    {sourceType === 'SO' ? (
                        <div className="space-y-2">
                            <RequiredLabel required htmlFor="so_id">Select Confirmed Sales Order</RequiredLabel>
                            <select
                                id="so_id"
                                {...register('so_id')}
                                aria-invalid={!!errors.so_id}
                                aria-required="true"
                                aria-describedby={errors.so_id ? 'so_id_error' : undefined}
                                className={cn("w-full rounded border p-2 text-sm", errors.so_id ? "border-red-500" : "border-slate-300")}
                            >
                                <option value="">Select Sales Order...</option>
                                {saleOrders.map(order => (
                                    <option key={order.so_id} value={order.so_id}>
                                        {formatSalesOrderRef(order.so_id)} : {order.customer_name || 'Customer'}{order.customer_po_no ? ` (${order.customer_po_no})` : ''}
                                    </option>
                                ))}
                            </select>
                            <FieldErrorText id="so_id_error" message={errors.so_id?.message} />
                            {saleOrders.length === 0 && <p className="text-xs text-amber-600">No confirmed sale orders available.</p>}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <RequiredLabel required htmlFor="estimate_id">Select Estimate / Quote Reference</RequiredLabel>
                            <select
                                id="estimate_id"
                                {...register('estimate_id')}
                                aria-invalid={!!errors.estimate_id}
                                aria-required="true"
                                aria-describedby={errors.estimate_id ? 'estimate_id_error' : undefined}
                                className={cn("w-full rounded border p-2 text-sm", errors.estimate_id ? "border-red-500" : "border-slate-300")}
                            >
                                <option value="">Select Estimate / Quote...</option>
                                {estimates.map(est => (
                                    <option key={est.estimate_id} value={est.estimate_id}>
                                        EST-{est.estimate_id} : {est.customer_name} ({est.total_price?.toFixed(2)})
                                    </option>
                                ))}
                            </select>
                            <FieldErrorText id="estimate_id_error" message={errors.estimate_id?.message} />
                            {estimates.length === 0 && <p className="text-xs text-amber-600">No estimate references available.</p>}
                        </div>
                    )}

                    <div className="space-y-2">
                        <RequiredLabel htmlFor="remarks">Remarks</RequiredLabel>
                        <textarea
                            id="remarks"
                            {...register('remarks')}
                            className="w-full rounded border border-slate-300 p-2 text-sm"
                            placeholder="Production notes..."
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isSubmitting ? 'Creating...' : 'Create Work Order'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
